// /portal/messages — real inbox view of every thread the client has access to.
//
// Replaces the previous "list of briefs that aren't delivered" page (which
// was misleading nav — it didn't show messages, it showed briefs).
//
// Driven by GET /api/messages/inbox which returns one row per request thread
// with last-message preview + unread count + total count, scoped to
// non-internal messages for client users.

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiGet, ApiError } from '../../lib/api';

interface ThreadRow {
  request_id: string;
  request_headline: string;
  request_status: string | null;
  client_id: string | null;
  client_name: string | null;
  last_message_at: string;
  last_preview: string;
  last_author_name: string;
  last_author_kind: 'staff' | 'client' | 'system';
  unread_count: number;
  total_count: number;
}

type Filter = 'all' | 'unread';

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diffSec = Math.round((now - then) / 1000);
  if (diffSec < 60) return `${diffSec}s ago`;
  if (diffSec < 3600) return `${Math.round(diffSec / 60)}m ago`;
  if (diffSec < 86_400) return `${Math.round(diffSec / 3600)}h ago`;
  if (diffSec < 7 * 86_400) return `${Math.round(diffSec / 86_400)}d ago`;
  return iso.slice(0, 10);
}

export function PortalMessages() {
  const [threads, setThreads] = useState<ThreadRow[] | null>(null);
  const [filter, setFilter] = useState<Filter>('all');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiGet<{ threads: ThreadRow[] }>('/api/messages/inbox')
      .then((r) => setThreads(r.threads))
      .catch((err: Error) => setError(err instanceof ApiError ? err.serverMessage : err.message));
  }, []);

  const list = useMemo(
    () => (threads ?? []).filter((t) => (filter === 'unread' ? t.unread_count > 0 : true)),
    [threads, filter],
  );

  const totalUnread = useMemo(
    () => (threads ?? []).reduce((s, t) => s + t.unread_count, 0),
    [threads],
  );

  return (
    <>
      <div>
        <h1 className="portal-h1">Messages</h1>
        <p className="portal-sub">
          One thread per brief. Click a row to read or reply — your engagement lead is on the other side.
        </p>
      </div>

      {error && (
        <div className="card" style={{ marginTop: 16, borderColor: 'var(--red)', color: 'var(--red)' }}>
          {error}
        </div>
      )}

      <div className="tabs" style={{ marginTop: 16 }}>
        <button className={filter === 'all' ? 'on' : ''} onClick={() => setFilter('all')}>
          All {threads !== null && <span className="tab-count">{threads.length}</span>}
        </button>
        <button className={filter === 'unread' ? 'on' : ''} onClick={() => setFilter('unread')}>
          Unread {totalUnread > 0 && <span className="tab-count">{totalUnread}</span>}
        </button>
      </div>

      {threads !== null && list.length === 0 && (
        <div className="card" style={{ marginTop: 16, textAlign: 'center', color: 'var(--fg-mute)' }}>
          {filter === 'unread'
            ? 'No unread messages. You\'re caught up.'
            : (
              <>
                No messages yet — start a thread by{' '}
                <Link to="/portal/requests/new" style={{ borderBottom: '1px solid var(--rule)' }}>
                  submitting a brief
                </Link>.
              </>
            )}
        </div>
      )}

      <div className="card flush" style={{ marginTop: 16 }}>
        {list.map((t) => (
          <Link
            key={t.request_id}
            to={`/portal/requests/${t.request_id}`}
            className="msg-row"
            style={{
              display: 'grid',
              gridTemplateColumns: '52px 1fr auto',
              gap: 14,
              padding: '14px 18px',
              borderBottom: '1px solid var(--rule)',
              textDecoration: 'none',
              color: 'inherit',
              alignItems: 'start',
              background: t.unread_count > 0 ? 'var(--bg-soft)' : 'transparent',
            }}
          >
            {/* Avatar — author kind initial */}
            <div
              aria-hidden="true"
              style={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                background: t.last_author_kind === 'staff' ? '#1a1a1a' : 'var(--accent)',
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 14,
                fontWeight: 600,
                marginTop: 2,
              }}
            >
              {t.last_author_name.split(/\s+/).slice(0, 2).map((s) => s[0]?.toUpperCase() ?? '').join('')}
            </div>

            {/* Headline + preview */}
            <div style={{ minWidth: 0 }}>
              <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', marginBottom: 2 }}>
                <strong style={{ fontSize: 14, fontWeight: t.unread_count > 0 ? 600 : 500 }}>
                  {t.request_headline}
                </strong>
                <span className="rid" style={{ fontSize: 11 }}>{t.request_id}</span>
                {t.request_status && (
                  <span style={{ fontSize: 11, color: 'var(--fg-mute)' }}>· {t.request_status}</span>
                )}
              </div>
              <div
                style={{
                  fontSize: 13,
                  color: t.unread_count > 0 ? 'var(--fg)' : 'var(--fg-soft)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                <span style={{ color: 'var(--fg-mute)' }}>
                  {t.last_author_kind === 'client' ? 'You' : t.last_author_name}:
                </span>{' '}
                {t.last_preview}
              </div>
            </div>

            {/* Right rail — timestamp + unread badge */}
            <div style={{ textAlign: 'right', whiteSpace: 'nowrap', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
              <span style={{ fontSize: 11, color: 'var(--fg-mute)', fontFamily: 'var(--mono)' }}>
                {relativeTime(t.last_message_at)}
              </span>
              {t.unread_count > 0 && (
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    background: 'var(--accent)',
                    color: '#fff',
                    padding: '2px 8px',
                    borderRadius: 999,
                    minWidth: 20,
                    textAlign: 'center',
                  }}
                >
                  {t.unread_count}
                </span>
              )}
            </div>
          </Link>
        ))}
      </div>
    </>
  );
}
